import * as React from "react"
import ChevronsIcon from "../../atoms/icons/ChevronsIcon"
import SettingsOverviewCard from "../../molecules/SettingsOverviewCard"
import ReadOnlyTextField from "../../molecules/card/ReadOnlyTextField"
import ButtonRow from "../../molecules/card/ButtonRow"
import ToggleButton from "../../atoms/inputs/ToggleButton"
import FileUploadIcon from "../../atoms/icons/FileUploadIcon"
import TextButton from "../../atoms/buttons/TextButton"
import ExternalLinkIcon from "../../atoms/icons/ExternalLinkIcon"
import DownloadingIcon from "../../atoms/icons/DownloadingIcon"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SoftwareUpdateModal from "../modals/SoftwareUpdateModal"
import SoftwareUpdateProgressModal from "../modals/SoftwareUpdateProgressModal"
import { isAvailableUpdateNewer } from "../../../utils/version-helpers"
import { SECTION_SYSTEM_SOFTWARE } from "../../../utils/pages"
import { useEdgeApi } from "../../../edge/EdgeProvider"
import { Link } from "react-router-dom"

interface SoftwareCardProps {
  version?: string
  latestOsVersionAvailable?: string
  isCheckingForUpdates?: boolean
  softwareUpdateModalOpen?: boolean
  useLocalUpdate?: boolean
  setIsCheckingForUpdates: (value: boolean) => void
  onSoftwareUpdateClick: () => void
  setInstallOsUpdateModalOpen: (value: boolean) => void
  setUseLocalUpdate: (value: boolean) => void
}

const SOFTWARE_TOOLTIP = `
Note: “Check for updates” will automatically search for new updates while connected to the internet, and notify if there are any.
Searching for and downloading updates will consume data.
A typical update is between 10 and 100MB.
Software updates will only be installed after being initiated by a user.
`

const SoftwareCard: React.FunctionComponent<SoftwareCardProps> = ({
  version: osVersion,
  latestOsVersionAvailable,
  isCheckingForUpdates,
  setIsCheckingForUpdates,
  softwareUpdateModalOpen,
  useLocalUpdate,
  onSoftwareUpdateClick,
  setUseLocalUpdate,
  setInstallOsUpdateModalOpen,
}) => {
  const { updateSoftware, softwareUpdateState } = useEdgeApi()
  const [initiateUpdate, setInitiateUpdate] = React.useState(false)

  const handleLocalSoftwareUpdateClick = () => {
    setUseLocalUpdate(true)
    setInstallOsUpdateModalOpen(true)
    // setInitiateUpdate(true)
  }

  const handleSoftwareUpdateConfirm = (file: File | null) => {
    if (file === null) {
      // Ota not implemented yet
      return
    }
    updateSoftware(file)
    setInstallOsUpdateModalOpen(false)
    setInitiateUpdate(true)
  }

  const handleToggleUpdates = () => {
    setIsCheckingForUpdates(!isCheckingForUpdates)
  }

  return (
    <>
      <SettingsOverviewCard title={SECTION_SYSTEM_SOFTWARE.name} icon={<ChevronsIcon />} tooltip={SOFTWARE_TOOLTIP}>
        <ReadOnlyTextField label="Software version" value={osVersion} placeholder="-.-.-" />
        <ButtonRow
          label="Check for updates"
          button={<ToggleButton active={isCheckingForUpdates} onToggle={handleToggleUpdates} />}
        />
        <ButtonRow
          label="Local update"
          button={
            <PrimaryButton
              size="small"
              variant="light"
              onClick={handleLocalSoftwareUpdateClick}
              iconLeft={<FileUploadIcon />}
              title="Browse"
            />
          }
        />
        <ButtonRow
          label="Open source licenses"
          button={
            <Link to="/licenses.json" reloadDocument={true} style={{ textDecoration: "none" }}>
              <TextButton icon={<ExternalLinkIcon />} title="Software list" />
            </Link>
          }
        />
        {latestOsVersionAvailable && osVersion && isAvailableUpdateNewer(latestOsVersionAvailable, osVersion) ? (
          <ButtonRow
            label={`An OS update (version ${latestOsVersionAvailable}) is available`}
            button={
              <PrimaryButton onClick={onSoftwareUpdateClick} iconLeft={<DownloadingIcon />} title="Install update" />
            }
          />
        ) : (
          <ReadOnlyTextField label="You're all up to date!" value="" />
        )}
      </SettingsOverviewCard>
      {softwareUpdateModalOpen && (
        <SoftwareUpdateModal
          open={softwareUpdateModalOpen}
          onClose={() => setInstallOsUpdateModalOpen(false)}
          onConfirm={handleSoftwareUpdateConfirm}
          version={latestOsVersionAvailable}
          isLocalUpdate={useLocalUpdate}
        />
      )}
      {!softwareUpdateModalOpen && initiateUpdate && softwareUpdateState !== undefined && (
        <SoftwareUpdateProgressModal softwareUpdateState={softwareUpdateState} />
      )}
    </>
  )
}

export default SoftwareCard
